import { FC, useCallback, useContext, useEffect, useMemo, useRef } from "react";

import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { isStripePaymentMethod, PAYMENT_METHOD_FROM_STRIPE, PaymentMethod } from "@simplyk/common";
import { PaymentElement as StripePaymentElement, useElements } from "@stripe/react-stripe-js";
import { LayoutObject, StripePaymentElementChangeEvent, StripePaymentElementOptions } from "@stripe/stripe-js";

import { FrontendFormContext } from "../../contexts/FrontendFormContext";
import { StripeContext } from "../../contexts/StripeContext";
import { STRIPE_PAYMENT_METHOD_FROM_PAYMENT_METHOD } from "../../providers/StripeProvider";
import { usePaymentErrorContext } from "../PaymentElement/PaymentErrorContext";

import {
  AchFormPaymentMethod,
  ChequeFormPaymentMethod,
  CreditCardFormPaymentMethod,
  PadFormPaymentMethod,
} from "@/forms-design-system/FormPaymentMethod";
import { CashFormPaymentMethod } from "@/forms-design-system/FormPaymentMethod/CashFormPaymentMethod";
import { getStripeAppearance } from "@/forms-design-system/FormPaymentMethod/FormPaymentMethodTokens";

interface PaymentMethodOptionsProps {
  offlinePaymentWording?: string | null;
}

export const PaymentMethodOptions: FC<PaymentMethodOptionsProps> = ({ offlinePaymentWording }) => {
  const theme = useTheme();
  const elements = useElements();

  const { setPaymentError } = usePaymentErrorContext();

  const { hasPad, hasAch, stripePaymentMethodTypes, stripePaymentMethods, zeffyPaymentMethods } =
    useContext(StripeContext);
  const {
    organization,
    prioritizeBankPayment,
    setSelectedPaymentMethod,
    selectedPaymentMethod,
    chequeDescription,
    displayExpressCheckout,
    expressCheckoutIsSelected,
  } = useContext(FrontendFormContext);

  const hasStripePaymentMethods = stripePaymentMethods.length > 0;
  const showCustomAccordion = stripePaymentMethods.length === 1 && zeffyPaymentMethods.length > 0;

  const collapseStripePaymentElement = useCallback(() => {
    elements?.getElement("payment")?.collapse();
  }, [elements]);

  // To get arround the limitation of updating the stripe payment method
  // we need to update the payment method types to only include the bank account
  // and then restore the original payment method types
  const restoreStripePaymentMethods = useRef(false);
  useEffect(() => {
    if (prioritizeBankPayment) {
      if (hasAch) {
        elements?.update({ paymentMethodTypes: ["us_bank_account"] });
        restoreStripePaymentMethods.current = true;
      }

      if (hasPad) {
        setSelectedPaymentMethod(PaymentMethod.Pad);
        collapseStripePaymentElement();
      }
    } else {
      // Reset to Card when prioritizeBankPayment is false
      setSelectedPaymentMethod(PaymentMethod.Card);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prioritizeBankPayment]);

  // We should collapse the stripe payment elements when express checkout is selected
  useEffect(() => {
    if (displayExpressCheckout && expressCheckoutIsSelected) {
      elements?.getElement("payment")?.collapse();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayExpressCheckout, expressCheckoutIsSelected]);

  const displayPadOnTop = prioritizeBankPayment && hasPad;
  useEffect(() => {
    if (stripePaymentMethods.length > 0 && zeffyPaymentMethods.length > 0) {
      const appearance = getStripeAppearance(theme, { hasMoreItemsAbove: displayPadOnTop, hasMoreItemsBelow: true });
      elements?.update({
        appearance,
      });
    }
  }, [
    displayPadOnTop,
    elements,
    selectedPaymentMethod,
    stripePaymentMethods,
    stripePaymentMethods.length,
    theme,
    theme.palette.text.brand.moderate,
    zeffyPaymentMethods,
    zeffyPaymentMethods.length,
  ]);

  const handleZeffyPaymentMethodChange = useCallback(
    (value: PaymentMethod) => {
      setPaymentError(null);
      collapseStripePaymentElement();
      setSelectedPaymentMethod(value);
    },
    [collapseStripePaymentElement, setPaymentError, setSelectedPaymentMethod]
  );

  const handleStripePaymentMethodChange = useCallback(
    (event: StripePaymentElementChangeEvent) => {
      // Ensure that only Stripe methods are handled here
      if (!isStripePaymentMethod(event.value.type)) {
        return;
      }

      // Avoid preselecting card when initially loading the form
      // + ignore additional events emitted by "handleZeffyPaymentMethodChange -> collapseStripePaymentElement" that select the card method while we want to change the payment method
      if (event.collapsed) {
        return;
      }

      setPaymentError(null);
      setSelectedPaymentMethod(PAYMENT_METHOD_FROM_STRIPE[event.value.type], event.complete);
    },
    [setPaymentError, setSelectedPaymentMethod]
  );

  const paymentMethodOptions: StripePaymentElementOptions = useMemo(
    () => ({
      business: { name: organization.name },
      fields: {
        billingDetails: {
          name: "never",
          email: "never",
          address: "never",
        },
      },
      wallets: { googlePay: "never", applePay: "never" },
      layout: {
        type: showCustomAccordion ? "tabs" : "accordion",
        defaultCollapsed: displayExpressCheckout,
        ...(!showCustomAccordion && { radios: true }), // radios is only available for accordion layout
      } as LayoutObject,
      paymentMethodOrder: prioritizeBankPayment
        ? [STRIPE_PAYMENT_METHOD_FROM_PAYMENT_METHOD.ach, STRIPE_PAYMENT_METHOD_FROM_PAYMENT_METHOD.card]
        : [STRIPE_PAYMENT_METHOD_FROM_PAYMENT_METHOD.card, STRIPE_PAYMENT_METHOD_FROM_PAYMENT_METHOD.ach],
    }),
    [organization.name, showCustomAccordion, displayExpressCheckout, prioritizeBankPayment]
  );

  const onReady = useCallback(() => {
    // Once the stripe elements are ready, we can restore the original payment method types after a short delay
    if (restoreStripePaymentMethods.current) {
      restoreStripePaymentMethods.current = false;
      setTimeout(() => {
        elements?.update({ paymentMethodTypes: stripePaymentMethodTypes });
      }, 600);
    }
  }, [elements, stripePaymentMethodTypes]);

  const stripePaymentElements = hasStripePaymentMethods && (
    <StripePaymentElement
      key={prioritizeBankPayment ? "prioritizeBankPayment" : "cardFirst"}
      id="payment-element"
      // When have a handler on our custom accordion, so this one becomes redundant
      onChange={showCustomAccordion ? undefined : handleStripePaymentMethodChange}
      options={paymentMethodOptions}
      onReady={onReady}
    />
  );

  const filteredZeffyPayMethods = useMemo(() => {
    if (displayPadOnTop) {
      return zeffyPaymentMethods.filter((method) => method !== PaymentMethod.Pad);
    }
    return zeffyPaymentMethods;
  }, [displayPadOnTop, zeffyPaymentMethods]);

  const renderPaymentMethod = ({
    paymentMethod,
    firstItem,
    lastItem,
    children,
  }: {
    paymentMethod: PaymentMethod;
    firstItem: boolean;
    lastItem: boolean;
    children?: React.ReactNode;
  }) => {
    switch (paymentMethod) {
      case PaymentMethod.Cheque:
        return (
          <ChequeFormPaymentMethod
            key={paymentMethod}
            firstItem={firstItem}
            lastItem={lastItem}
            checked={selectedPaymentMethod === paymentMethod}
            customMessage={chequeDescription ?? undefined}
            onChange={() => handleZeffyPaymentMethodChange(paymentMethod)}
            data-test="payment-method-cheque"
          />
        );
      case PaymentMethod.Pad:
        return (
          <PadFormPaymentMethod
            key={paymentMethod}
            checked={selectedPaymentMethod === paymentMethod}
            firstItem={firstItem}
            lastItem={lastItem}
            onChange={() => handleZeffyPaymentMethodChange(paymentMethod)}
            data-test="payment-method-pad"
          />
        );
      case PaymentMethod.Ach:
        return (
          <AchFormPaymentMethod
            key={paymentMethod}
            checked={selectedPaymentMethod === paymentMethod}
            firstItem={firstItem}
            lastItem={lastItem}
            onChange={() => handleZeffyPaymentMethodChange(paymentMethod)}
            data-test={paymentMethod.toLowerCase()}
          >
            {children}
          </AchFormPaymentMethod>
        );
      case PaymentMethod.Card:
        return (
          <CreditCardFormPaymentMethod
            key={paymentMethod}
            checked={selectedPaymentMethod === paymentMethod}
            firstItem={firstItem}
            lastItem={lastItem}
            onChange={() => handleZeffyPaymentMethodChange(paymentMethod)}
            data-test={paymentMethod.toLowerCase()}
          >
            {children}
          </CreditCardFormPaymentMethod>
        );

      case PaymentMethod.Cash:
        return (
          <CashFormPaymentMethod
            key={paymentMethod}
            checked={selectedPaymentMethod === paymentMethod}
            label={offlinePaymentWording ?? undefined}
            firstItem={firstItem}
            lastItem={lastItem}
            onChange={() => handleZeffyPaymentMethodChange(paymentMethod)}
            data-test={paymentMethod.toLowerCase()}
          />
        );

      default:
        throw new Error(`Unsupported payment method: ${paymentMethod}`);
    }
  };

  return (
    <Box data-test="payment-elements-wrapper">
      {displayPadOnTop &&
        renderPaymentMethod({
          paymentMethod: PaymentMethod.Pad,
          firstItem: true,
          lastItem: !hasStripePaymentMethods && filteredZeffyPayMethods.length === 0,
        })}
      {showCustomAccordion
        ? renderPaymentMethod({
            paymentMethod: stripePaymentMethods[0],
            firstItem: !displayPadOnTop,
            lastItem: filteredZeffyPayMethods.length === 0,
            children: stripePaymentElements,
          })
        : stripePaymentElements}

      {filteredZeffyPayMethods.length > 0 && (
        <Box
          sx={{
            ...(stripePaymentMethods.length > 1 && {
              marginTop: "-5px",
            }),
          }}
        >
          {filteredZeffyPayMethods.map((paymentMethod, index) =>
            renderPaymentMethod({
              paymentMethod,
              firstItem: !stripePaymentMethods.length && !displayPadOnTop,
              lastItem: index + 1 === filteredZeffyPayMethods.length,
            })
          )}
        </Box>
      )}
    </Box>
  );
};
